import styled, { css } from "styled-components";

export const MobileToggle = styled.button`
  align-items: center;
  backdrop-filter: blur(16px);
  background: rgba(18, 22, 28, 0.92);
  border: none;
  border-radius: 1rem;
  color: var(--brand-ink);
  display: flex;
  flex-shrink: 0;
  height: 2.5rem;
  justify-content: center;
  width: 2.5rem;

  &:hover {
    background: rgba(120, 167, 222, 0.08);
  }
`;

export const MobileOverlay = styled.button`
  background: rgba(4, 6, 9, 0.72);
  inset: 0;
  position: fixed;
  z-index: 30;
`;

export const Sidebar = styled.aside<{ $isOpen: boolean }>`
  background: rgba(15, 20, 27, 0.98);
  border-right: none;
  box-shadow: var(--brand-sidebar-shadow);
  backdrop-filter: blur(22px);
  color: white;
  display: flex;
  flex-direction: column;
  height: 100vh;
  inset: 0 auto 0 0;
  max-height: 100vh;
  min-height: 100vh;
  overflow: hidden;
  position: fixed;
  transform: translateX(${({ $isOpen }) => ($isOpen ? "0" : "-100%")});
  transition: transform 300ms ease-in-out;
  width: min(20rem, calc(100vw - 0.75rem));
  z-index: 40;

  @media (min-width: 640px) {
    width: min(21.5rem, calc(100vw - 1rem));
  }

  @media (min-width: 1024px) {
    transform: translateX(0);
    width: 16.5rem;
  }

  @media (min-width: 1280px) {
    width: 18.75rem;
  }
`;

export const BrandArea = styled.div`
  flex-shrink: 0;
  padding: 3.25rem 0.875rem 0.375rem;

  @media (min-width: 1024px) {
    padding: 1rem 0.875rem 0.375rem;
  }

  @media (min-width: 1280px) {
    padding: 1.25rem 1rem 0.5rem;
  }
`;

export const MenuScroll = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0.75rem 0.875rem 1rem;

  @media (min-width: 1024px) {
    padding-top: 0.25rem;
    padding-bottom: 1rem;
  }

  @media (min-width: 1280px) {
    padding: 0.25rem 1rem 1rem;
  }
`;

export const BrandDivider = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border-radius: 1.5rem;
  padding: 1rem;
`;

export const BrandRow = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 0.875rem;
`;

export const BrandMark = styled.div`
  align-items: center;
  background: rgba(240, 191, 86, 0.12);
  border-radius: 1.2rem;
  display: flex;
  flex-shrink: 0;
  height: 3.25rem;
  justify-content: center;
  padding: 0.55rem;
  width: 3.25rem;
`;

export const WorkspaceRow = styled.div`
  margin-top: 0.625rem;
  width: 100%;
`;

export const WorkspaceBadge = styled.div`
  align-items: center;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 999px;
  color: var(--brand-highlight);
  display: inline-flex;
  font-size: 0.75rem;
  letter-spacing: 0.16em;
  line-height: 1.2;
  max-width: 100%;
  padding: 0.4rem 0.75rem;
  text-transform: uppercase;
  word-break: break-word;
`;

export const SectionEyebrow = styled.p`
  color: var(--brand-muted);
  font-family: var(--font-anonymous, inherit);
  font-size: 0.75rem;
  letter-spacing: 0.22em;
  margin-top: 0;
  padding: 0 0.25rem;
  text-transform: uppercase;
`;

export const GroupList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.25rem;
`;

export const GroupCard = styled.li<{ $active?: boolean; $open?: boolean }>`
  background: ${({ $active, $open }) =>
    $active
      ? "rgba(255, 255, 255, 0.05)"
      : $open
      ? "rgba(255, 255, 255, 0.035)"
      : "rgba(255, 255, 255, 0.02)"};
  border-radius: 1.25rem;
  list-style: none;
  transition:
    background 160ms ease,
    transform 160ms ease;

  &:hover {
    background: ${({ $active }) =>
      $active ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.04)"};
  }
`;

export const GroupButton = styled.button<{ $active?: boolean }>`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 1.1rem;
  color: ${({ $active }) => ($active ? "var(--brand-ink)" : "rgba(245, 239, 230, 0.88)")};
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
  padding: 0.85rem 0.85rem 0.75rem;
  text-align: left;
  transition: color 160ms ease;
  width: 100%;

  &:hover {
    color: var(--brand-ink);
  }
`;

export const GroupCopy = styled.div`
  flex: 1;
  min-width: 0;
`;

export const GroupTitle = styled.p`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.01em;
`;

export const GroupActionCluster = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: 0.45rem;
`;

export const GroupChevron = styled.span<{ $isOpen?: boolean }>`
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 999px;
  color: inherit;
  display: inline-flex;
  flex-shrink: 0;
  font-size: 0.75rem;
  height: 1.75rem;
  justify-content: center;
  transform: rotate(${({ $isOpen }) => ($isOpen ? "180deg" : "0deg")});
  transition:
    transform 160ms ease,
    background 160ms ease;
  width: 1.75rem;
`;

export const GroupChildren = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 0;
  padding: 0 0.35rem 0.35rem;
`;

const itemLinkBase = css<{ $active?: boolean }>`
  align-items: center;
  background: ${({ $active }) =>
    $active ? "rgba(120, 167, 222, 0.1)" : "transparent"};
  border: none;
  border-radius: 1rem;
  color: ${({ $active }) => ($active ? "var(--brand-ink)" : "rgba(245, 239, 230, 0.88)")};
  cursor: pointer;
  display: flex;
  gap: 0.65rem;
  min-height: 2.75rem;
  padding: 0.625rem 0.75rem 0.625rem 0.9rem;
  position: relative;
  text-align: left;
  transition:
    background 160ms ease,
    color 160ms ease,
    transform 160ms ease;
  width: 100%;

  &::before {
    background: var(--brand-highlight);
    border-radius: 999px;
    content: "";
    height: 1.2rem;
    left: 0.3rem;
    opacity: ${({ $active }) => ($active ? 1 : 0)};
    position: absolute;
    top: 50%;
    transform: translateY(-50%) scaleY(${({ $active }) => ($active ? 1 : 0.4)});
    transition:
      opacity 160ms ease,
      transform 160ms ease;
    width: 3px;
  }

  &:hover {
    background: ${({ $active }) =>
      $active ? "rgba(120, 167, 222, 0.1)" : "rgba(255, 255, 255, 0.05)"};
    color: ${({ $active }) => ($active ? "var(--brand-ink)" : "white")};
    transform: translateX(2px);
  }
`;

export const ItemLink = styled.button.attrs({ type: "button" })<{ $active?: boolean }>`
  ${itemLinkBase}
`;

export const ItemIcon = styled.span<{ $active?: boolean }>`
  align-items: center;
  background: ${({ $active }) =>
    $active ? "var(--brand-highlight)" : "rgba(255, 255, 255, 0.05)"};
  border-radius: 0.85rem;
  color: ${({ $active }) => ($active ? "#1b1306" : "rgba(245, 239, 230, 0.88)")};
  display: flex;
  flex-shrink: 0;
  font-size: 0.92rem;
  height: 2.1rem;
  justify-content: center;
  transition:
    background 160ms ease,
    color 160ms ease;
  width: 2.1rem;
`;

export const ItemLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.2rem;
`;
